import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { TextSelect, Toggle } from '@app/form';
import { Quote, QuoteProspectInfo, QuoteStatus } from 'queries';
import { supportedLocales } from 'i18n';

const useStyles = createUseStyles((theme: Theme) => ({
  infos: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.marginBase * 4,
    alignItems: 'flex-end',
  },
  info: {
    ...theme.fonts.caption,
    color: theme.colors.inkSecondary,
  },
  infoValue: {
    ...theme.fonts.label,
    color: theme.colors.inkPrimary,
  },
  locale: {
    width: 200,
  },
  status: {
    display: 'flex',
    gap: theme.marginBase * 2,
  },
  statusSelector: {
    width: 300,
  },
  vatBox: {
    display: 'flex',
    flexDirection: 'column',
  },
}));

const capitalize = (value: string) =>
  value.charAt(0).toUpperCase() + value.slice(1);

interface Props {
  quote: Quote;
  prospect?: QuoteProspectInfo | null;
  locale: string;
  onLocaleChange: (locale: string) => void;
  vat: boolean;
  onVatToggle: (checked: boolean) => void;
  onStatusChange: (status: string) => void;
}

export const QuoteSidebar = ({
  quote,
  prospect,
  locale,
  onLocaleChange,
  vat,
  onVatToggle,
  onStatusChange,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const localeOptions = useMemo(
    () =>
      supportedLocales.map((value) => ({
        value,
        label: capitalize(
          new Intl.DisplayNames([value], { type: 'language' }).of(value) ||
            value,
        ),
      })),
    [],
  );

  const statusOptions = useMemo(
    () =>
      Object.values(QuoteStatus).map((status) => ({
        value: status,
        label: t(`quote.statuses.${status}`),
      })),
    [t],
  );

  return (
    <div className={classes.infos}>
      <div className={classes.locale}>
        <TextSelect
          noMargin
          label={t('quote.language')}
          options={localeOptions}
          value={locale}
          onChange={onLocaleChange}
        />
      </div>
      <div className={classes.status}>
        <TextSelect
          containerClass={classes.statusSelector}
          noMargin
          label={t('quote.status')}
          options={statusOptions}
          value={quote.status}
          onChange={onStatusChange}
        />
        <Toggle
          containerClass={classes.vatBox}
          noMargin
          checked={vat}
          onToggle={onVatToggle}
          label={t('quote.vat')}
          labelBefore
        />
      </div>
      <div>
        <div className={classes.info}>{t('quote.quote.company')}</div>
        <div className={classes.infoValue}>{prospect?.companyName || '/'}</div>
      </div>
      <div>
        <div className={classes.info}>{t('quote.quote.prospect')}</div>
        <div className={classes.infoValue}>
          {prospect?.firstName + ' ' + prospect?.lastName || '/'}
        </div>
      </div>
      <div>
        <div className={classes.info}>{t('quote.quote.email')}</div>
        <div className={classes.infoValue}>{prospect?.email || '/'}</div>
      </div>
      <div>
        <div className={classes.info}>{t('quote.billingCountry')}</div>
        <div className={classes.infoValue}>
          {quote.billingCountry || prospect?.billingCountryCode || '/'}
        </div>
      </div>
      <div>
        <div className={classes.info}>{t('quote.studyType')}</div>
        <div className={classes.infoValue}>{quote.studyType || '/'}</div>
      </div>
      <div>
        <div className={classes.info}>{t('quote.duration')}</div>
        <div className={classes.infoValue}>
          {quote.durationDisplayed || `${quote.duration} min`}
        </div>
      </div>
    </div>
  );
};
